/* ============================================================
 * MyEbike predictive search
 * -----------------------------------------------------------
 * Attaches to any search input inside `.mb-header__search`,
 * fetches /search/suggest?section_id=predictive-search and
 * overlays a branded dropdown below the search input. No
 * dependency on WS-A's header Liquid — hooks by selector so
 * WS-A can ship first.
 *
 * Keyboard support: arrow up/down to navigate, enter to open,
 * escape to close.
 * ============================================================ */

(function() {
  'use strict';

  var ATTACHED = new WeakSet();
  var SELECTOR = '.mb-header__search';
  var MIN_LEN  = 2;
  var DEBOUNCE = 160;

  function ready(cb) {
    if (document.readyState !== 'loading') { cb(); }
    else { document.addEventListener('DOMContentLoaded', cb); }
  }

  function debounce(fn, wait) {
    var t;
    return function() {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(ctx, args); }, wait);
    };
  }

  function createPanel(form) {
    var panel = document.createElement('div');
    panel.className = 'mb-psearch-panel';
    panel.setAttribute('hidden', '');
    panel.innerHTML = '<div class="mb-psearch-panel__spinner" role="status" aria-live="polite"><span></span></div>';
    form.style.position = form.style.position || 'relative';
    form.appendChild(panel);
    return panel;
  }

  function setSelected(panel, idx, options) {
    options.forEach(function(opt, i) {
      if (i === idx) {
        opt.setAttribute('aria-selected', 'true');
        var link = opt.querySelector('a,button');
        if (link) link.focus({ preventScroll: true });
      } else {
        opt.setAttribute('aria-selected', 'false');
      }
    });
  }

  function attach(form) {
    if (ATTACHED.has(form)) return;
    ATTACHED.add(form);

    var input = form.querySelector('input[type="search"]');
    if (!input) return;

    // Ensure aria scaffolding for the combobox pattern
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', 'predictive-search-results');
    input.setAttribute('autocomplete', 'off');

    var panel   = createPanel(form);
    var abortCtl = null;
    var lastTerm = '';
    var isOpen   = false;

    function close() {
      if (!isOpen) return;
      panel.setAttribute('hidden', '');
      panel.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      isOpen = false;
    }
    function open() {
      if (isOpen) return;
      panel.removeAttribute('hidden');
      panel.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
      isOpen = true;
    }

    function render(markup) {
      panel.innerHTML = markup;
      // Clicking anywhere inside the panel should NOT blur-close before the link fires
      panel.addEventListener('mousedown', function(e) { e.preventDefault(); });
      open();
    }

    function loading() {
      panel.innerHTML = '<div class="mb-psearch-panel__spinner" role="status" aria-live="polite"><span></span></div>';
      open();
    }

    function fetchResults(term) {
      if (abortCtl) abortCtl.abort();
      abortCtl = new AbortController();

      var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      var url  = base + '?q=' + encodeURIComponent(term) + '&section_id=predictive-search';

      loading();

      fetch(url, { signal: abortCtl.signal })
        .then(function(res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function(text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('#shopify-section-predictive-search');
          if (section) {
            render(section.innerHTML || '<div class="mb-psearch__empty"><p class="mb-psearch__empty-sub">No matches yet. Keep typing.</p></div>');
          }
        })
        .catch(function(err) {
          if (err && err.name === 'AbortError') return;
          panel.innerHTML = '<div class="mb-psearch__empty"><p class="mb-psearch__empty-sub">Couldn\'t load suggestions. <a href="/search?q=' + encodeURIComponent(term) + '" class="mb-acct-link">Search everything →</a></p></div>';
          open();
        });
    }

    var onInput = debounce(function() {
      var term = (input.value || '').trim();
      if (term === lastTerm) return;
      lastTerm = term;
      if (term.length < MIN_LEN) { close(); return; }
      fetchResults(term);
    }, DEBOUNCE);

    input.addEventListener('input', onInput);
    input.addEventListener('focus', function() {
      if ((input.value || '').trim().length >= MIN_LEN) open();
    });

    // Keyboard nav
    input.addEventListener('keydown', function(e) {
      if (!isOpen) return;
      var options = Array.prototype.slice.call(panel.querySelectorAll('[role="option"]'));
      if (options.length === 0) return;

      var current = options.findIndex(function(o) { return o.getAttribute('aria-selected') === 'true'; });

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected(panel, (current + 1) % options.length, options);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected(panel, (current <= 0 ? options.length - 1 : current - 1), options);
      } else if (e.key === 'Enter') {
        if (current >= 0) {
          var link = options[current].querySelector('a, button');
          if (link) {
            e.preventDefault();
            link.click();
          }
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
        input.focus();
      }
    });

    // Close on outside click
    document.addEventListener('click', function(e) {
      if (!form.contains(e.target) && isOpen) close();
    });
    // Close on form blur (outside panel/form)
    input.addEventListener('blur', function() {
      setTimeout(function() {
        if (!form.contains(document.activeElement)) close();
      }, 150);
    });

    // Pressing Enter on the form with no selection navigates to full search
    form.addEventListener('submit', function() {
      close();
    });
  }

  function attachAll() {
    document.querySelectorAll(SELECTOR).forEach(attach);
  }

  ready(function() {
    attachAll();

    // Handle elements injected later (section re-renders, theme editor)
    var obs = new MutationObserver(function(records) {
      for (var r of records) {
        for (var n of r.addedNodes) {
          if (!(n instanceof HTMLElement)) continue;
          if (n.matches && n.matches(SELECTOR)) attach(n);
          n.querySelectorAll && n.querySelectorAll(SELECTOR).forEach(attach);
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  });
})();
